/**
 * Portal member status (members list + member detail) — same labels as the API / SQL view.
 * Use full-string checks (case-insensitive on the whole label), not substring matching.
 */

import { MemberListItemDto } from '../models/member-list.model';

/** Canonical labels from `vw_ext_member_base` / `vw_ext_member_products`. */
export const PortalMemberStatus = {
  Active: 'Active',
  ActiveUntil: 'Active Until',
  ActiveStarting: 'Active Starting',
  CancellationRequested: 'Cancellation Requested',
  LeaveOfAbsence: 'Leave of Absence',
  CoverageEnded: 'Coverage Ended',
  LapsedForNonPayment: 'Lapsed for Non-Payment',
  Lapsed: 'Lapsed',
  Deferred: 'Deferred',
  Hold: 'Hold',
  Unknown: 'Unknown'
} as const;

export type PortalMemberStatusLabel = (typeof PortalMemberStatus)[keyof typeof PortalMemberStatus];

/**
 * Statuses returned when the member list is filtered by {@link PortalMemberStatus.Active}
 * (API expands Active via ActiveOnlyStatuses).
 */
export const PORTAL_MEMBER_ACTIVE_FAMILY_STATUSES = [
  PortalMemberStatus.Active,
  PortalMemberStatus.ActiveUntil,
  PortalMemberStatus.ActiveStarting
] as const;

/**
 * Member list status filter options (excludes Unknown).
 * Selecting Active includes the full {@link PORTAL_MEMBER_ACTIVE_FAMILY_STATUSES} set on the API.
 */
export const PORTAL_MEMBER_STATUS_FILTER_OPTIONS = [
  PortalMemberStatus.Active,
  PortalMemberStatus.CancellationRequested,
  PortalMemberStatus.LeaveOfAbsence,
  PortalMemberStatus.CoverageEnded,
  PortalMemberStatus.LapsedForNonPayment,
  PortalMemberStatus.Lapsed,
  PortalMemberStatus.Deferred,
  PortalMemberStatus.Hold
] as const;

export type PortalMemberStatusFilterOption = (typeof PORTAL_MEMBER_STATUS_FILTER_OPTIONS)[number];

function normStatus(s: string | null | undefined): string {
  return (s ?? '').trim().toLowerCase();
}

const N = {
  active: normStatus(PortalMemberStatus.Active),
  activeUntil: normStatus(PortalMemberStatus.ActiveUntil),
  activeStarting: normStatus(PortalMemberStatus.ActiveStarting),
  cancellationRequested: normStatus(PortalMemberStatus.CancellationRequested),
  leaveOfAbsence: normStatus(PortalMemberStatus.LeaveOfAbsence),
  coverageEnded: normStatus(PortalMemberStatus.CoverageEnded),
  lapsedForNonPayment: normStatus(PortalMemberStatus.LapsedForNonPayment),
  lapsed: normStatus(PortalMemberStatus.Lapsed),
  deferred: normStatus(PortalMemberStatus.Deferred),
  hold: normStatus(PortalMemberStatus.Hold),
  unknown: normStatus(PortalMemberStatus.Unknown)
} as const;

/**
 * CSS class suffix for the status badge (e.g. `status-active` — use with `[ngClass]="'status-' + class"` or map below).
 * Full-phrase matches only.
 */
export function portalMemberStatusBadgeClass(status: string): string {
  const s = normStatus(status);
  if (s === '' || s === '—' || s === '-') {
    return 'status-unknown';
  }
  if (s === N.active) {
    return 'status-active';
  }
  if (s === N.activeUntil) {
    return 'status-active-until';
  }
  if (s === N.activeStarting) {
    return 'status-active-starting';
  }
  if (s === N.cancellationRequested) {
    return 'status-cancellation-requested';
  }
  if (s === N.leaveOfAbsence || s === N.lapsed) {
    return 'status-leave-of-absence';
  }
  if (s === N.coverageEnded) {
    return 'status-coverage-ended';
  }
  if (s === N.lapsedForNonPayment) {
    return 'status-lapsed-non-payment';
  }
  if (s === N.unknown) {
    return 'status-unknown';
  }
  return 'status-unknown';
}

/** "Last, First". Falls back to whichever part exists, then to the API's Name. */
export function formatMemberNameLastFirst(
  m: Pick<MemberListItemDto, 'firstName' | 'lastName' | 'name'>
): string {
  const first = (m.firstName ?? '').trim();
  const last = (m.lastName ?? '').trim();
  if (last && first) return `${last}, ${first}`;
  return last || first || (m.name ?? '').trim();
}

/**
 * When to show a date next to the status chip (list, header, product table).
 * — **Active Starting**: `effectiveDate` (coverage starts)
 * — **Active Until** / **Coverage Ended**: coverage end
 */
export function portalStatusShowsSideDate(status: string | null | undefined): boolean {
  const s = normStatus(status);
  return s === N.activeStarting || s === N.activeUntil || s === N.coverageEnded || s === N.cancellationRequested;
}

/**
 * @deprecated use {@link portalStatusShowsSideDate} — this name only reflected coverage end; Active Starting is included in {@link portalStatusShowsSideDate}.
 */
export function portalStatusShowsCoverageEndDateLabel(status: string | null | undefined): boolean {
  const s = normStatus(status);
  return s === N.activeUntil || s === N.coverageEnded || s === N.cancellationRequested;
}

/**
 * Date string to show beside member-level status (members list + member detail header).
 * — **Active Starting** → `effectiveDate`
 * — **Active Until** / **Coverage Ended** → `coverageExpDate` or `coverageExpirationDate`
 * Pass `skipActiveStarting` where the effective date is already shown next to the status (members list).
 */
export function portalMemberCoverageEndDateForDisplay(
  m: {
    status?: string | null;
    effectiveDate?: string | null;
    coverageExpDate?: string | null;
    coverageExpirationDate?: string | null;
  },
  options?: { skipActiveStarting?: boolean }
): string | null {
  const s = normStatus(m.status);
  if (s === N.activeStarting) {
    return options?.skipActiveStarting ? null : pickNonEmptyString(m.effectiveDate);
  }
  if (s === N.activeUntil || s === N.coverageEnded || s === N.cancellationRequested) {
    return pickNonEmptyString(m.coverageExpDate ?? m.coverageExpirationDate);
  }
  return null;
}

function pickNonEmptyString(v: string | null | undefined): string | null {
  if (v == null) {
    return null;
  }
  const t = String(v).trim();
  return t === '' ? null : t;
}

/**
 * True when product status is **Coverage Ended** (coverage expiration already passed).
 */
export function portalProductStatusIsCoverageEnded(status: string | null | undefined): boolean {
  return normStatus(status) === N.coverageEnded;
}

/**
 * Member detail product table: do not show rate columns for **Coverage Ended** rows.
 */
export function portalProductStatusHidesRates(status: string | null | undefined): boolean {
  return portalProductStatusIsCoverageEnded(status);
}
